import { useState, useEffect } from 'react';
import { X, Building2, Tag, DollarSign, Calendar, FileText, Check, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import { safeToISOString } from '../lib/dateUtils';
import ModalContainer from './layout/ModalContainer.tsx';
import { FormGroup, FormLabel, FormInput, FormSelect } from './ui/FormComponents';
import { apiClient } from '../api/client';

interface FinanceEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry?: any;
  onCreate?: (data: any) => Promise<any>;
  onUpdate?: (id: string, data: any) => Promise<any>;
  onDelete?: (id: string) => Promise<any>;
}

export default function FinanceEntryModal({ isOpen, onClose, entry, onCreate, onUpdate, onDelete }: FinanceEntryModalProps) {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('RECEITA');
  const [status, setStatus] = useState('PENDENTE');
  const [category, setCategory] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [isRecurrent, setIsRecurrent] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && entry) {
      setDescription(entry.description || '');
      setAmount(entry.amount?.toString() || '');
      setType(entry.type || 'RECEITA');
      setStatus(entry.status || 'PENDENTE');
      setCategory(entry.category || '');
      setCompanyId(entry.companyId?.toString() || '');
      setProjectId(entry.projectId?.toString() || '');
      setIsRecurrent(entry.isRecurrent || false);
      setDueDate(entry.dueDate ? safeToISOString(entry.dueDate).split('T')[0] : '');
      setPaymentDate(entry.paymentDate ? safeToISOString(entry.paymentDate).split('T')[0] : '');
    } else if (!isOpen) {
      resetForm();
    }
  }, [isOpen, entry]);

  const resetForm = () => {
    setDescription('');
    setAmount('');
    setCategory('');
    setType('RECEITA');
    setStatus('PENDENTE');
    setCompanyId('');
    setProjectId('');
    setIsRecurrent(false);
    setDueDate('');
    setPaymentDate('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description || !amount) return;

    setLoading(true);
    try {
      const payload = {
        description,
        amount: parseFloat(amount),
        type,
        category,
        companyId: companyId || null,
        projectId: projectId || null,
        isRecurrent,
        dueDate: dueDate || null,
        paymentDate: paymentDate || null,
        status,
      };

      if (entry) {
        if (onUpdate) await onUpdate(entry.id, payload);
      } else {
        if (onCreate) await onCreate(payload);
      }
      onClose();
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar lançamento.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!entry || !onDelete) return;
    if (!confirm('Deseja excluir este lançamento?')) return;
    setLoading(true);
    try {
      await onDelete(entry.id);
      onClose();
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir lançamento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} maxWidth="max-w-xl">
      <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <DollarSign size={14} strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#111111]">
              {entry ? 'Editar Lançamento' : 'Novo Lançamento Financeiro'}
            </h3>
            <p className="text-[10px] font-medium text-[#64748B] mt-0.5">
              {entry ? 'Atualize as informações da transação' : 'Registre uma nova movimentação no caixa'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 flex-1 overflow-y-auto flex flex-col gap-5 text-left">
        <FormGroup>
          <FormLabel required>Descrição</FormLabel>
          <FormInput required placeholder="Ex: Pagamento AWS / Consultoria Tech" value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormGroup>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormGroup>
            <FormLabel required>Valor (R$)</FormLabel>
            <FormInput type="number" step="0.01" required placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </FormGroup>

          <FormGroup>
            <FormLabel>Tipo</FormLabel>
            <FormSelect value={type} onChange={(e) => setType(e.target.value)}>
              <option value="RECEITA">Receita (+)</option>
              <option value="DESPESA">Despesa (-)</option>
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>Status</FormLabel>
            <FormSelect value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="PAGO">Pago</option>
              <option value="PENDENTE">Pendente</option>
              <option value="ATRASADO">Atrasado</option>
            </FormSelect>
          </FormGroup>
        </div>

        <FormGroup>
          <FormLabel>Categoria</FormLabel>
          <FormInput placeholder="Ex: Infraestrutura, Salários, Freelance" value={category} onChange={(e) => setCategory(e.target.value)} />
        </FormGroup>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-2 pt-4">
            <input type="checkbox" id="recurrent" checked={isRecurrent} onChange={(e) => setIsRecurrent(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="recurrent" className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider cursor-pointer">Lançamento Recorrente</label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup>
            <FormLabel>Data de Vencimento</FormLabel>
            <FormInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </FormGroup>
          {status === 'PAGO' && (
            <FormGroup>
              <FormLabel>Data de Pagamento</FormLabel>
              <FormInput type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            </FormGroup>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#0F172A05]">
          <div>
            {entry && (
              <button type="button" onClick={handleDelete} disabled={loading} className="text-rose-500 hover:text-rose-700 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors">
                <Trash2 size={12} /> Excluir Lançamento
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer">Cancelar</button>
            <button type="submit" disabled={loading || !description || !amount} className="px-5 py-2 text-xs font-bold text-white bg-[#111111] hover:bg-[#222222] rounded-xl shadow-sm hover:scale-[1.01] transition-all cursor-pointer flex items-center gap-1.5">
              {loading ? 'Salvando...' : (
                <>
                  <Check size={14} strokeWidth={2.5} />
                  <span>{entry ? 'Salvar Lançamento' : 'Registrar Lançamento'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </ModalContainer>
  );
}
